import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TYPES = ["hot_work", "height", "confined_space", "electrical", "mechanical", "excavation"];
const PPE = ["Helmet", "Safety Shoes", "Gloves", "Harness", "Face Shield", "Respirator", "Ear Plugs"];

export default function Permits() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const isSafety = ["safety_officer", "company_admin", "super_admin"].includes(user?.role);
  const [form, setForm] = useState({ project_id: "", description: "", location: "", permit_types: [], hazards: "", risk_controls: "", ppe_checklist: [], loto_required: false, gas_test_required: false, gas_test_readings: "", valid_from: "", valid_to: "" });

  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); }); load(); }, []);
  const load = () => api.get("/permits").then((r) => setList(r.data));

  const arrToggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const submit = async () => {
    if (!form.description) return toast.error("Description is required");
    try { await api.post("/permits", form); toast.success("Permit requested"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const act = async (id, action) => {
    let body = { action };
    if (action === "reject") { const r = prompt("Rejection reason:"); if (!r) return; body.rejection_reason = r; }
    try { await api.patch(`/permits/${id}`, body); toast.success(`Permit ${action}d`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = list.filter((p) => !q || `${p.permit_number} ${p.description}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout title="Permit to Work" subtitle="HSE work permits — request, approval & closure">
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="Request Permit" addTestId="permit-add-button" searchTestId="permit-search-input" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Permit #</TableHead><TableHead>Types</TableHead><TableHead>Description</TableHead>
            <TableHead>Requested By</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filt.map((p) => (
              <TableRow key={p.id} data-testid={`permit-row-${p.permit_number}`}>
                <TableCell className="font-mono font-semibold text-xs">{p.permit_number}</TableCell>
                <TableCell className="text-xs">{(p.permit_types || []).join(", ") || "—"}</TableCell>
                <TableCell className="max-w-xs truncate">{p.description}</TableCell>
                <TableCell className="text-xs text-slate-500">{p.requested_by}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {isSafety && p.status === "requested" && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs mr-1" onClick={() => act(p.id, "approve")} data-testid={`permit-approve-${p.permit_number}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs mr-1 text-red-600" onClick={() => act(p.id, "reject")} data-testid={`permit-reject-${p.permit_number}`}>Reject</Button>
                    </>
                  )}
                  {p.status === "approved" && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => act(p.id, "close")} data-testid={`permit-close-${p.permit_number}`}>Close</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No permits</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="permit-create-dialog">
          <DialogHeader><DialogTitle>Request Permit to Work</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-cap">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="permit-project-input"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label className="label-cap">Description *</Label><Input data-testid="permit-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2"><Label className="label-cap">Location</Label><Input data-testid="permit-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="label-cap">Permit Types</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {TYPES.map((t) => (
                  <label key={t} className="flex items-center gap-1.5 text-xs border rounded px-2 py-1 cursor-pointer">
                    <Checkbox checked={form.permit_types.includes(t)} onCheckedChange={() => arrToggle("permit_types", t)} />{t.replace("_", " ")}
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2"><Label className="label-cap">Hazard Identification</Label><Textarea data-testid="permit-hazards-input" value={form.hazards} onChange={(e) => setForm({ ...form, hazards: e.target.value })} /></div>
            <div className="col-span-2"><Label className="label-cap">Risk Controls</Label><Textarea data-testid="permit-controls-input" value={form.risk_controls} onChange={(e) => setForm({ ...form, risk_controls: e.target.value })} /></div>
            <div className="col-span-2">
              <Label className="label-cap">PPE Checklist</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PPE.map((t) => (
                  <label key={t} className="flex items-center gap-1.5 text-xs border rounded px-2 py-1 cursor-pointer">
                    <Checkbox checked={form.ppe_checklist.includes(t)} onCheckedChange={() => arrToggle("ppe_checklist", t)} />{t}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.loto_required} onCheckedChange={(v) => setForm({ ...form, loto_required: !!v })} data-testid="permit-loto-input" /> LOTO Required</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.gas_test_required} onCheckedChange={(v) => setForm({ ...form, gas_test_required: !!v })} data-testid="permit-gas-input" /> Gas Test Required</label>
            <div><Label className="label-cap">Valid From</Label><Input type="date" data-testid="permit-from-input" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
            <div><Label className="label-cap">Valid To</Label><Input type="date" data-testid="permit-to-input" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="permit-submit-button">Submit request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
